import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { AppDispatch, RootState } from "../../redux/store";
import {
  fetchAllNamespacesHealth,
} from "../../redux/actions/assetActions";
import { NamespaceSummary, NamespacePair } from "../../types/memoryHealth";
import SegmentedHealthBar from "./memory-health/SegmentedHealthBar";

interface UnifiedNamespaceCardProps {
  pair: NamespacePair;
  onNamespaceClick: (namespace: string) => void;
}

const UnifiedNamespaceCard: React.FC<UnifiedNamespaceCardProps> = ({
  pair,
  onNamespaceClick,
}) => {
  const devStats = pair.dev_stats;
  const stableStats = pair.stable_stats;

  const getStatusIcon = (stats: any) => {
    if (!stats || stats.total_assets === 0) return null;

    const hasIssues = stats.needs_rebuild_count > 0 || stats.error_count > 0;
    const healthPercentage =
      stats.total_assets > 0
        ? (stats.healthy_count / stats.total_assets) * 100
        : 0;

    if (hasIssues) {
      return <ErrorIcon style={{ fontSize: 16, color: "#ef4444" }} />;
    } else if (healthPercentage === 100) {
      return <CheckCircleIcon style={{ fontSize: 16, color: "#22c55e" }} />;
    } else {
      return <WarningIcon style={{ fontSize: 16, color: "#f59e0b" }} />;
    }
  };

  const getStatusTooltip = (stats: any, isStable: boolean) => {
    if (!stats || stats.total_assets === 0) return null;

    // Always show tooltip with raw status counts
    return (
      <div style={{ padding: "4px" }}>
        <div
          style={{ fontWeight: 600, fontSize: "0.75rem", marginBottom: "4px" }}
        >
          {isStable ? "Production" : "Development"} Status
        </div>
        <div
          style={{
            fontSize: "0.7rem",
            marginBottom: "4px",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: "4px",
          }}
        >
          Total: {stats.total_assets}
        </div>

        {/* Show all available status counts directly from stats */}
        {stats.healthy_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            healthy: {stats.healthy_count}
          </div>
        )}
        {stats.processing_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            processing: {stats.processing_count}
          </div>
        )}
        {stats.version_mismatch_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            version_mismatch: {stats.version_mismatch_count}
          </div>
        )}
        {stats.missing_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            missing: {stats.missing_count}
          </div>
        )}
        {stats.needs_rebuild_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            needs_rebuild: {stats.needs_rebuild_count}
          </div>
        )}
        {stats.not_published_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            not_published: {stats.not_published_count}
          </div>
        )}
        {stats.deleted_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            deleted: {stats.deleted_count}
          </div>
        )}
        {stats.crashed_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            crashed: {stats.crashed_count}
          </div>
        )}
        {stats.schema_outdated_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            schema_outdated: {stats.schema_outdated_count}
          </div>
        )}
        {stats.error_count > 0 && (
          <div style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
            error: {stats.error_count}
          </div>
        )}
      </div>
    );
  };

  const renderStatusBox = (
    stats: any,
    isProduction: boolean,
    namespace?: string
  ) => {
    const hasData = stats && stats.total_assets > 0;

    const boxStyle: React.CSSProperties = {
      padding: "12px",
      borderRadius: "6px",
      backgroundColor: hasData ? "#ffffff" : "#fafafa",
      border: hasData ? "1px solid #e2e8f0" : "1px dashed #d4d4d8",
      minHeight: "106px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "100%",
      boxShadow: hasData ? "0 1px 3px rgba(0, 0, 0, 0.05)" : "none",
    };

    return (
      <div style={boxStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                fontWeight: 600,
                color: hasData
                  ? isProduction
                    ? "#0369a1"
                    : "#64748b"
                  : "#a1a1aa",
                fontSize: "0.7rem",
                letterSpacing: "0.025em",
              }}
            >
              {isProduction ? "PRODUCTION" : "DEVELOPMENT"}
            </span>
            {hasData && (
              <Tooltip
                title={getStatusTooltip(stats, isProduction)}
                arrow
                placement="top"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "help",
                  }}
                >
                  {getStatusIcon(stats)}
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Segmented Health bar */}
        <div style={{ width: "100%", margin: "8px 0" }}>
          {hasData ? (
            <SegmentedHealthBar
              stats={stats}
              namespace={namespace || ""}
              height={6}
            />
          ) : (
            <div
              style={{
                height: "6px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
              }}
            />
          )}
        </div>

        {/* Footer */}
        {hasData ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ color: "#64748b", fontSize: "0.65rem" }}>
              {stats.healthy_count}/{stats.total_assets} healthy
            </span>
            <Button
              size="small"
              startIcon={<VisibilityIcon style={{ fontSize: 14 }} />}
              onClick={() => namespace && onNamespaceClick(namespace)}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                py: 0.25,
                px: 1,
                minHeight: 0,
              }}
            >
              View
            </Button>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              marginTop: "8px",
            }}
          >
            <span style={{ color: "#a1a1aa", fontSize: "0.65rem" }}>
              No {isProduction ? "production" : "development"} version
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card
      sx={{
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header - Fixed height with 2-line wrap and ellipsis */}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "0.9rem",
            marginBottom: 2,
            minHeight: "2.4rem", // Space for 2 lines
            maxHeight: "2.4rem",
            lineHeight: "1.2rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {pair.base_name}
        </Typography>

        {/* Status Row using flexbox with proper spacing */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          {/* Development Box - Always first */}
          <div style={{ flex: 1, display: "flex" }}>
            {renderStatusBox(devStats, false, pair.dev_namespace)}
          </div>

          {/* Production Box - Always second */}
          <div style={{ flex: 1, display: "flex" }}>
            {renderStatusBox(stableStats, true, pair.stable_namespace)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MemoryHealthTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaired, setShowPaired] = useState(true);
  const [onlyWithIssues, setOnlyWithIssues] = useState(true);

  const { error, memoryHealth } = useSelector(
    (state: RootState) => state.assets
  );
  const { allNamespacesHealth, allNamespacesLoading } = memoryHealth;

  useEffect(() => {
    // Fetch with appropriate filters
    dispatch(
      fetchAllNamespacesHealth({
        groupPairs: showPaired,
        onlyWithIssues,
      })
    );
  }, [dispatch, showPaired, onlyWithIssues]);

  const handleNamespaceClick = (namespace: string) => {
    // Just navigate - let the MemoryHealthView component handle data fetching
    // This prevents race conditions between navigation and API calls
    navigate(`/dashboard/memory-health/${namespace}`);
  };

  const handleRefresh = () => {
    dispatch(
      fetchAllNamespacesHealth({
        groupPairs: showPaired,
        onlyWithIssues,
      })
    );
  };

  // Filter namespaces based on search and remove empty namespaces
  const filteredPairs = React.useMemo(() => {
    if (!allNamespacesHealth?.namespace_pairs) return [];

    return allNamespacesHealth.namespace_pairs
      .filter((pair: NamespacePair) => {
        // Filter out pairs where both dev and stable have 0 assets
        const devHasAssets = pair.dev_stats && pair.dev_stats.total_assets > 0;
        const stableHasAssets =
          pair.stable_stats && pair.stable_stats.total_assets > 0;
        if (!devHasAssets && !stableHasAssets) return false;

        // Apply search filter
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
          pair.base_name.toLowerCase().includes(search) ||
          pair.dev_namespace.toLowerCase().includes(search) ||
          (pair.stable_namespace &&
            pair.stable_namespace.toLowerCase().includes(search))
        );
      })
      .sort((a, b) => {
        // Priority 1: Missing assets (biggest error class)
        const aMissing =
          (a.dev_stats?.missing_count || 0) +
          (a.stable_stats?.missing_count || 0);
        const bMissing =
          (b.dev_stats?.missing_count || 0) +
          (b.stable_stats?.missing_count || 0);

        if (aMissing > bMissing) return -1;
        if (aMissing < bMissing) return 1;

        // Priority 2: Crashed assets
        const aCrashed =
          (a.dev_stats?.crashed_count || 0) +
          (a.stable_stats?.crashed_count || 0);
        const bCrashed =
          (b.dev_stats?.crashed_count || 0) +
          (b.stable_stats?.crashed_count || 0);

        if (aCrashed > bCrashed) return -1;
        if (aCrashed < bCrashed) return 1;

        // Priority 3: Other issues (errors, needs_rebuild, etc.)
        const aOtherIssues =
          (a.dev_stats?.error_count || 0) +
          (a.stable_stats?.error_count || 0) +
          (a.dev_stats?.needs_rebuild_count || 0) +
          (a.stable_stats?.needs_rebuild_count || 0) +
          (a.dev_stats?.version_mismatch_count || 0) +
          (a.stable_stats?.version_mismatch_count || 0) +
          (a.dev_stats?.schema_outdated_count || 0) +
          (a.stable_stats?.schema_outdated_count || 0);
        const bOtherIssues =
          (b.dev_stats?.error_count || 0) +
          (b.stable_stats?.error_count || 0) +
          (b.dev_stats?.needs_rebuild_count || 0) +
          (b.stable_stats?.needs_rebuild_count || 0) +
          (b.dev_stats?.version_mismatch_count || 0) +
          (b.stable_stats?.version_mismatch_count || 0) +
          (b.dev_stats?.schema_outdated_count || 0) +
          (b.stable_stats?.schema_outdated_count || 0);

        if (aOtherIssues > bOtherIssues) return -1;
        if (aOtherIssues < bOtherIssues) return 1;

        // Priority 4: Any non-healthy assets
        const aHealthy =
          (a.dev_stats
            ? a.dev_stats.healthy_count === a.dev_stats.total_assets
            : true) &&
          (a.stable_stats
            ? a.stable_stats.healthy_count === a.stable_stats.total_assets
            : true);
        const bHealthy =
          (b.dev_stats
            ? b.dev_stats.healthy_count === b.dev_stats.total_assets
            : true) &&
          (b.stable_stats
            ? b.stable_stats.healthy_count === b.stable_stats.total_assets
            : true);

        if (!aHealthy && bHealthy) return -1;
        if (aHealthy && !bHealthy) return 1;

        // Final: Sort by name
        return a.base_name.localeCompare(b.base_name);
      });
  }, [allNamespacesHealth?.namespace_pairs, searchTerm]);

  const filteredNamespaces = React.useMemo(() => {
    if (!allNamespacesHealth?.namespaces) return [];

    return allNamespacesHealth.namespaces
      .filter((ns: NamespaceSummary) => {
        // Filter out namespaces with 0 assets
        if (ns.total_assets === 0) return false;

        // Apply search filter
        if (!searchTerm) return true;
        return ns.namespace.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => {
        // Priority 1: Missing assets (biggest error class)
        const aMissing = a.missing_count || 0;
        const bMissing = b.missing_count || 0;
        if (aMissing > bMissing) return -1;
        if (aMissing < bMissing) return 1;

        // Priority 2: Crashed assets
        const aCrashed = a.crashed_count || 0;
        const bCrashed = b.crashed_count || 0;
        if (aCrashed > bCrashed) return -1;
        if (aCrashed < bCrashed) return 1;

        // Priority 3: Other issues
        const aOtherIssues =
          a.error_count +
          a.needs_rebuild_count +
          (a.version_mismatch_count || 0) +
          (a.schema_outdated_count || 0);
        const bOtherIssues =
          b.error_count +
          b.needs_rebuild_count +
          (b.version_mismatch_count || 0) +
          (b.schema_outdated_count || 0);

        if (aOtherIssues > bOtherIssues) return -1;
        if (aOtherIssues < bOtherIssues) return 1;

        // Priority 4: Any non-healthy assets
        const aHealthy = a.healthy_count === a.total_assets;
        const bHealthy = b.healthy_count === b.total_assets;

        if (!aHealthy && bHealthy) return -1;
        if (aHealthy && !bHealthy) return 1;

        // Final: Sort by name
        return a.namespace.localeCompare(b.namespace);
      });
  }, [allNamespacesHealth?.namespaces, searchTerm]);

  // Calculate summary statistics (excluding empty namespaces)
  const summaryStats = React.useMemo(() => {
    if (!allNamespacesHealth?.namespaces) {
      return { total: 0, withIssues: 0, nonEmpty: 0 };
    }

    // Filter out empty namespaces for stats
    const nonEmptyNamespaces = allNamespacesHealth.namespaces.filter(
      (ns: NamespaceSummary) => ns.total_assets > 0
    );

    const nonEmpty = nonEmptyNamespaces.length;
    const withIssues = nonEmptyNamespaces.filter(
      (ns: NamespaceSummary) => ns.needs_rebuild_count > 0 || ns.error_count > 0
    ).length;

    return {
      total: allNamespacesHealth.namespaces.length,
      withIssues,
      nonEmpty,
    };
  }, [allNamespacesHealth?.namespaces]);

  if (allNamespacesLoading || !allNamespacesHealth) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Stack spacing={2}>
            {/* Title Row */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MemoryIcon sx={{ color: "#4299e1", fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#2d3748",
                      fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    }}
                  >
                    Memory Health Monitor
                  </Typography>
                </Box>

                {/* Summary Stats */}
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${summaryStats.nonEmpty} namespaces`}
                    size="small"
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      fontWeight: 500,
                    }}
                  />
                  {summaryStats.withIssues > 0 && (
                    <Chip
                      icon={<WarningIcon sx={{ fontSize: 16 }} />}
                      label={`${summaryStats.withIssues} with issues`}
                      size="small"
                      sx={{
                        backgroundColor: "#fef3c7",
                        color: "#78350f",
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Stack>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search namespaces..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 250,
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e0",
                      },
                    },
                  }}
                />

                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      backgroundColor: "#f7fafc",
                      "&:hover": { backgroundColor: "#edf2f7" },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* Filter Controls */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={showPaired}
                    onChange={(e) => setShowPaired(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#4a5568" }}>
                    Group dev/stable pairs
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyWithIssues}
                    onChange={(e) => setOnlyWithIssues(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#4a5568" }}>
                    Only show issues
                  </Typography>
                }
              />
            </Stack>

            {error && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {error}
              </Alert>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3, backgroundColor: "#f7fafc" }}>
        {showPaired && filteredPairs.length > 0 ? (
          // Paired View
          <Grid container spacing={2}>
            {filteredPairs.map((pair: NamespacePair) => (
              <Grid item xs={12} md={6} lg={4} key={pair.base_name}>
                <UnifiedNamespaceCard
                  pair={pair}
                  onNamespaceClick={handleNamespaceClick}
                />
              </Grid>
            ))}
          </Grid>
        ) : !showPaired && filteredNamespaces.length > 0 ? (
          // Unpaired View - Convert to unified cards
          <Grid container spacing={2}>
            {filteredNamespaces.map((ns: NamespaceSummary) => {
              // Create a temporary pair for unpaired namespaces
              const tempPair: NamespacePair = {
                base_name: ns.namespace.replace("_stable", ""),
                dev_namespace: ns.is_stable ? "" : ns.namespace,
                stable_namespace: ns.is_stable ? ns.namespace : undefined,
                dev_stats: ns.is_stable ? undefined : ns,
                stable_stats: ns.is_stable ? ns : undefined,
              };

              return (
                <Grid item xs={12} md={6} lg={4} key={ns.namespace}>
                  <UnifiedNamespaceCard
                    pair={tempPair}
                    onNamespaceClick={handleNamespaceClick}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          // Empty State
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <Typography variant="h6" sx={{ color: "#718096", mb: 1 }}>
              No namespaces found
            </Typography>
            <Typography variant="body2" sx={{ color: "#a0aec0" }}>
              {searchTerm
                ? "Try adjusting your search criteria"
                : onlyWithIssues
                ? "No namespaces with issues found"
                : "No namespaces available"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MemoryHealthTab;
